/**
 * Registry of public marketing feature pages.
 *
 * Single source of truth for:
 *   - the `/features/:slug/` routes (App.tsx)
 *   - the MarketingNav "Features" dropdown
 *   - public/sitemap.xml (manually mirrored)
 *
 * Keep copy on-brand: Excerptus is the HEART of a Knowledge Worker's second brain,
 * never a full second-brain replacement. Don't position against Obsidian/Notion/
 * Roam/Claude — Excerptus complements them.
 */
import {
  Library,
  BookMarked,
  PenLine,
  Sparkles,
  Image as ImageIcon,
  Rss,
  Mic,
  StickyNote,
  Send,
  Compass,
  Brain,
  Quote,
  Search,
  ShieldCheck,
  Download,
  FileText,
  Tags,
  Workflow,
  Headphones,
  Layers,
  Users,
  DollarSign,
  Building2,
  type LucideIcon,


} from "lucide-react";

export type FeatureBlock = {
  icon: LucideIcon;
  title: string;
  body: string;
  /** When true, the block spans the full grid row on tablet + desktop. */
  wide?: boolean;
};

export type FeatureFaqEntry = {
  q: string;
  a: string;
};

export type FeatureGroup = "capture" | "organize" | "publish" | "earn" | "comingSoon";

export type FeaturePage = {
  slug: string;
  group: FeatureGroup;
  navLabel: string;
  navDescription: string;
  /** Browser <title> */
  title: string;
  /** Meta description */
  description: string;
  /** Tiny eyebrow above the H1 */
  eyebrow: string;
  /** Primary H1 (keyword-leaning) */
  h1: string;
  /** Sub-headline (North Star voice) */
  sub: string;
  /** Optional badge shown next to the H1 (e.g. "v2 preview") */
  badge?: string;
  /** Path the primary CTA should link to. Defaults to /auth/?tab=signup */
  ctaHref?: string;
  /** Primary CTA label. Defaults to "Start free trial" */
  ctaLabel?: string;
  /** Optional secondary CTA (link + label) */
  secondaryCta?: { href: string; label: string };
  /** Feature blocks: 3–5 per page */
  blocks: FeatureBlock[];
  /** Optional callout section between blocks and FAQ */
  callout?: { title: string; body: string; href?: string; linkLabel?: string };
  /** FAQ entries: 3–4 per page (used for accordion + FAQPage JSON-LD) */
  faq: FeatureFaqEntry[];
  /** Slugs to cross-link at the bottom (2–3) */
  crossLinks: string[];
  /** Render the v2 notify form instead of the trial CTA */
  notifyForm?: { source: string; helper: string };
  /** When true, the page is registered + routable but hidden from the
   *  MarketingNav mega-menu. Used for meta/stance pages that are linked
   *  from the homepage rather than browsed via the nav. */
  omitFromNav?: boolean;
};

const TRIAL_CTA = { ctaHref: "/auth/?tab=signup", ctaLabel: "Start free trial" } as const;

export const FEATURE_PAGES: FeaturePage[] = [
  // --- 1 ----------------------------------------------------------------
  {
    slug: "readwise-sync",
    group: "capture",
    navLabel: "Readwise Sync",
    navDescription: "Pull every highlight into your second brain, every day.",
    title: "Readwise Sync — Excerptus",
    description:
      "Sync Readwise highlights into Excerptus daily. Every book auto-promotes to your library; every highlight stays linked to its source. Included on every plan.",
    eyebrow: "Capture",
    h1: "Readwise Highlights, Synced to Your Second Brain",
    sub: "Excerptus pulls every Readwise book and highlight into a dedicated, searchable inbox — and auto-creates the matching library item so citations are ready the moment you write.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: BookMarked,
        title: "Daily sync, zero effort",
        body: "Connect once with a Readwise token; a background job pulls new highlights every morning. Manual re-sync is one click away.",
      },
      {
        icon: Library,
        title: "Books promote to your library",
        body: "Every Readwise book becomes a library item with author, title, and cover. Citations resolve automatically — no copy-paste.",
      },
      {
        icon: Quote,
        title: "Highlights stay linked to their source",
        body: "Open any highlight, see its book card, and jump straight into an excerpt that already knows where it came from.",
      },
      {
        icon: ShieldCheck,
        title: "Token encrypted at rest",
        body: "Your Readwise API token is encrypted with a workspace key. Disconnect any time from Settings → Integrations.",
      },
    ],
    faq: [
      {
        q: "Which plans include Readwise sync?",
        a: "Every plan, Starter through Agency. There's no add-on or upgrade required.",
      },
      {
        q: "How often does it sync?",
        a: "Automatically once a day, and on-demand whenever you press 'Sync now' on the Readwise Highlights page.",
      },
      {
        q: "Does Excerptus modify highlights in Readwise?",
        a: "No. Sync is read-only — Excerptus pulls from Readwise but never pushes back.",
      },
      {
        q: "What if I disconnect?",
        a: "Disconnecting deletes your stored token. Existing highlights stay in your workspace; new ones simply stop arriving until you reconnect.",
      },
    ],
    crossLinks: ["my-library", "my-excerpts", "feed-reader"],
  },

  // --- 2 ----------------------------------------------------------------
  {
    slug: "image-studio",
    group: "publish",
    navLabel: "Image Studio",
    navDescription: "Turn excerpts into branded share images in seconds.",
    title: "Image Studio — Excerptus",
    description:
      "Generate branded, share-ready images from any excerpt. Solid, brand, or gradient backgrounds, attribution, and your own brand mark — built into Excerptus.",
    eyebrow: "Publish",
    h1: "Turn Excerpts into Branded Share Images",
    sub: "Image Studio takes any saved excerpt and renders it on a solid color, your brand swatches, or a custom gradient — with attribution, citation, and your corner mark already baked in.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: ImageIcon,
        title: "From excerpt to image in seconds",
        body: "Pick a saved excerpt, choose a background (solid color, brand swatch, or gradient), and Image Studio handles typography, attribution, and crop.",
      },
      {
        icon: Sparkles,
        title: "Brand mark of your own",
        body: "Replace the default Excerptus corner mark with your own logo. Save brand colors as reusable swatches so you can apply them to any image without re-picking hex codes.",
      },
      {
        icon: Quote,
        title: "Attribution pulled from your library",
        body: "Author, source title, and citation are pulled from the linked library item — the same source of truth your bibliography uses.",
      },
    ],
    callout: {
      title: "More Image Tools Coming",
      body: "Image Studio is tabbed — Excerpt Image is the first tool, with audiograms and more in the queue.",
    },
    faq: [
      {
        q: "Do I need a separate image tool?",
        a: "No. Image Studio replaces the round-trip to Canva or Figma for the most common share-image use case.",
      },
      {
        q: "What background options do I get?",
        a: "Solid colors, your saved brand swatches, or a custom linear or radial gradient — with an overlay control to keep text legible.",
      },
      {
        q: "What about my logo?",
        a: "Upload it once in Image Studio settings; it replaces the Excerptus corner mark on every image, until you replace or clear it.",
      },
    ],

    crossLinks: ["my-excerpts", "composer", "my-library"],
  },

  // --- 3 ----------------------------------------------------------------
  {
    slug: "feed-reader",
    group: "capture",
    navLabel: "Feed Reader",
    navDescription: "An RSS inbox built for blog reading / research.",
    title: "Feed Reader — Excerptus",
    description:
      "A focused RSS/Atom inbox inside Excerptus. Read, mark important, promote a feed item straight into your citable library.",
    eyebrow: "Capture",
    h1: "An RSS feed reader built for research",
    sub: "A quiet RSS/Atom inbox for the blogs and publications that actually feed your work — scan on your schedule, then promote the keepers into your citable library.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: Rss,
        title: "Subscribe to RSS or Atom",
        body: "Paste a feed URL and Excerptus pulls new items daily. No 'For You' algorithm — just the feeds you chose, in chronological order.",
      },
      {
        icon: Quote,
        title: "Cite a blog post as a reference",
        body: "Promote any feed item into My Library as a website source — author, URL, and date carry over — then cite it from an excerpt or Composer Piece like any other reference.",
      },
      {
        icon: BookMarked,
        title: "Save-to-Library promotion",
        body: "When something is worth keeping, promote it to a library source in one click — author, URL, and publication date carry over, ready for excerpts.",
      },
      {
        icon: ShieldCheck,
        title: "Per-workspace caps, gentle by design",
        body: "10 feeds on Starter, 30 on Plus, 50 on Pro and above — generous enough to be useful, capped to stay calm.",
      },
    ],
    faq: [
      {
        q: "Is this a Feedly replacement?",
        a: "For research, often yes. Feed Reader is intentionally minimal — it's built to feed your library, not to be your full reading app.",
      },
      {
        q: "How often does it sync?",
        a: "Once a day automatically. You can also refresh a single feed on demand from its detail page.",
      },
      {
        q: "Can I use a blog post as a citation in my writing?",
        a: "Yes. Promote the feed item to My Library and it becomes a website-type source you can attach to excerpts and cite from any Composer Piece, with the URL and access date already filled in.",
      },
      {
        q: "What happens when I promote a feed item?",
        a: "Excerptus creates a library item of type 'website' with the article's URL, author (when available), and date — ready for excerpts and citations.",
      },
    ],
    crossLinks: ["my-library", "readwise-sync", "podcasts-feed-reader"],
  },

  // --- 4 ----------------------------------------------------------------
  {
    slug: "podcasts-feed-reader",
    group: "capture",
    navLabel: "Podcasts",
    navDescription: "Discover Podcasts. Capture Transcripts. Save What Matters.",
    title: "Podcasts — Excerptus",
    description:
      "Discover podcasts from the PodcastIndex catalog, listen in-app with word-level transcripts, and promote episodes worth citing straight to your library.",
    eyebrow: "Capture",
    h1: "Discover Podcasts. Capture Transcripts. Save What Matters.",
    sub: "Excerptus podcasts is for listening with a notebook open. Discover shows, read along when the publisher ships a transcript (or generate one on demand), and promote any episode into your citable library.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: Headphones,
        title: "Discover from PodcastIndex",
        body: "Search by term, category, or language across the open PodcastIndex catalog. No closed ecosystem, no walled garden.",
      },
      {
        icon: Mic,
        title: "Publisher transcripts — free",
        body: "When a show ships a transcript with its feed, Excerptus loads it instantly at no credit cost. Read along with the audio and click any word to jump the player to that point.",
      },
      {
        icon: Sparkles,
        title: "AI transcripts on demand",
        body: "No publisher transcript? Optionally generate one with Deepgram Nova-3 using your Excerptus AI credits. Handy when you want to save the episode for citation; skippable since longer episodes use more credits.",
      },
      {
        icon: BookMarked,
        title: "Promote-to-Library",
        body: "Found the episode you want to cite? Promote it as a library source and excerpt the transcript like any other text.",
      },
      {
        icon: ShieldCheck,
        title: "Per-workspace caps",
        body: "10 subscriptions on Starter, 30 on Plus, 50 on Pro and above. Daily sync handles new episodes in the background.",
      },
    ],
    callout: {
      title: "Excerptus Is Not a Podcast Host",
      body: "This feature is for listening to and citing other people's podcasts — discovery, transcripts, and capture. We don't host or publish your show.",
    },
    faq: [
      {
        q: "Does Excerptus host my podcast?",
        a: "No. Excerptus is a listening-and-research tool. Hosting your own show stays with whichever host you already use.",
      },
      {
        q: "Are transcripts free?",
        a: "Reading a publisher-attached transcript is free and instant — no credits used. When the publisher doesn't ship one, you can optionally generate a transcript with Deepgram Nova-3 using your Excerptus AI credits. It's optional because longer episodes use more credits; use it when you actually want to save the episode for citation.",
      },
      {
        q: "Can I excerpt straight from the transcript?",
        a: "Yes — promote the episode to a library item and use the transcript text as your excerpt body. The library item carries the episode metadata as the citation source.",
      },
    ],
    crossLinks: ["my-library", "feed-reader", "readwise-sync"],
  },

  // --- 5 ----------------------------------------------------------------
  {
    slug: "composer",
    group: "publish",
    navLabel: "Composer",
    navDescription: "One calm writing surface for your long-form work.",
    title: "Composer — Excerptus",
    description:
      "Write long-form Pieces grounded in your own library, excerpts, and Knowledge Worker Codex. AI optional — never required.",
    eyebrow: "Publish",
    h1: "One Writing Surface for Your\nLong-form Work",
    sub: "The Composer is the main writing surface in Excerptus — built for the core work. Your library, your excerpts, your Codex, and your publishing destinations are all one click away while you write. Nothing to context-switch into.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: PenLine,
        title: "Everything in one surface",
        body: "Draft, research, and cite without breaking flow. Your library, excerpts, notes, and Codex sit right alongside the editor — pull what you need, keep writing, then hand off to the Publisher panel when you're ready to ship.",
      },
      {
        icon: Library,
        title: "Grounded in your Corpus",
        body: "Promote your strongest work into the Corpus so future Drafts cite your own canon first — not generic web text.",
      },
      {
        icon: Brain,
        title: "Knowledge Worker Codex",
        body: "Topics, voice, writing rules, and publish-type tones all live in the Codex. Every Draft inherits them automatically.",
      },
      {
        icon: Send,
        title: "Unified publishing",
        body: "WordPress, Kit, Bento, Substack, Buttondown, and more — one Piece, many destinations, one publish action.",
      },
      {
        icon: ShieldCheck,
        title: "AI is optional, always",
        body: "Turn it off and the Composer is a calm long-form editor. Turn it on and you get planning and writing agents grounded in your own work.",
      },
    ],
    callout: {
      title: "Pair with the Knowledge Worker Codex",
      body: "The Codex is what makes Composer feel like you — voice, taxonomy, and rules baked in.",
      href: "/features/knowledge-worker-codex/",
      linkLabel: "See the Codex →",
    },
    faq: [
      {
        q: "Do I have to use AI in the Composer?",
        a: "No. The Composer is a complete long-form editor without AI. The agents are additive — flip them on when you want them, off when you don't.",
      },
      {
        q: "What's the difference between a Piece and a Draft?",
        a: "A Piece is the container — the project. A Draft is one writing pass within that Piece. You can have multiple Drafts on a single Piece.",
      },
      {
        q: "Where do my finished pieces go?",
        a: "Every published Piece lives in the Archive, organized in folders you create. Pieces you want the agents to learn from get promoted to the Corpus.",
      },
      {
        q: "Which destinations can Composer publish to?",
        a: "WordPress, Kit (ConvertKit), Bento, Substack, Buttondown, and manual destinations you define. The set grows as adapters land.",
      },
    ],
    crossLinks: ["knowledge-worker-codex", "my-library", "my-excerpts"],
  },

  // --- 6 ----------------------------------------------------------------
  {
    slug: "knowledge-worker-codex",
    group: "organize",
    navLabel: "My KW Codex",
    navDescription: "An optional Heart-core that guides the AI side of Excerptus — your topics, your voice, your avatars. Use all of it, or just the parts you want. (Optional)",
    title: "Knowledge Worker Codex — Excerptus",
    description:
      "The optional Heart-core of Excerptus: a Topic Tree, a Voice profile, and Audience Avatars that guide every AI moment. Fully modular — fill in only the parts you want.",
    eyebrow: "Organize",
    h1: "Your Knowledge Worker Codex",
    sub: "The Codex is the optional Heart-core of Excerptus. It's where you define the Topic Tree that organizes your excerpts and Corpus, the Voice the AI should draft in, and the Avatars it should aim for. It's also fully modular — fill in only the parts you want. Skip Voice and Avatars and Excerptus simply won't apply them; build out the Topic Tree alone and you've still got a clean taxonomy across your excerpts and Corpus.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: Tags,
        title: "Topics that mean something",
        body: "The Codex-driven Topic Tree is a pure organization layer too: attach topic chips to individual excerpts in My Excerpts and to documents in My Corpus, then filter and group by what the work is actually about — no AI required.",
      },
      {
        icon: Brain,
        title: "Voice profile (optional)",
        body: "Train a Voice profile from your real writing and the Writing Agent will lean on it for AI-assisted Drafts. Leave it blank and Excerptus simply doesn't apply one.",
      },
      {
        icon: Workflow,
        title: "Audience Avatars (optional)",
        body: "Describe the audiences you write for. When present, the AI uses them to frame Drafts. When absent, it just doesn't.",
      },
    ],
    faq: [
      {
        q: "Is the Codex required?",
        a: "No. Excerptus runs end-to-end with the Codex off. With it on, every part is independently optional — fill in just the Topic Tree, just Voice, just Avatars, or all three.",
      },
      {
        q: "Can I disable the Codex per workspace?",
        a: "Yes — there's a per-workspace switch in Settings. Turn it off for a side project where you don't want any of it in play; turn it back on and everything you'd built is still there.",
      },
      {
        q: "How does the voice profile work?",
        a: "Feed in samples of your real writing. The profile distills voice, sentence rhythm, and pet phrases — and the Writing Agent uses it as a system prompt when you draft with AI on.",
      },
    ],
    crossLinks: ["composer", "my-excerpts", "my-library"],
  },

  // --- 7 ----------------------------------------------------------------
  {
    slug: "my-library",
    group: "organize",
    navLabel: "My Library",
    navDescription: "A citable research library that never loses a source.",
    title: "My Library — Excerptus",
    description:
      "A proper research library: books, articles, chapters, sites, reports, podcasts — with ISBNdb, DOI, and Open Library resolution, plus optional Amazon enrichment.",
    eyebrow: "Organize",
    h1: "A Citable Research Library That Never Loses a Source",
    sub: "My Library is where every book, article, chapter, website, report, and podcast you cite actually lives — with real metadata, real citations, and zero copy-paste.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: Library,
        title: "Six source types, one shape",
        body: "Books, journal articles, chapters, websites, reports, and other — each with the fields citation styles actually need. No more rectangle-into-circle.",
      },
      {
        icon: Search,
        title: "Source search built in",
        body: "Look up by title, ISBN, DOI, or URL. Excerptus pulls metadata from ISBNdb, OpenAlex, and Crossref so author, year, and publisher are correct on entry.",
      },
      {
        icon: Quote,
        title: "Citations resolve to CSL-JSON",
        body: "Every library item exports as proper CSL-JSON for shortCite, footnotes, endnotes, and bibliographies that survive academic scrutiny.",
      },
    ],
    faq: [
      {
        q: "How is this different from Zotero?",
        a: "Different goals. Zotero is a citation manager. My Library is the source-of-truth layer underneath Excerpts and the Composer — citations are one job it does, not its only one.",
      },
      {
        q: "Can I import an existing library?",
        a: "Today, Excerptus pulls from Readwise (books auto-promote) and resolves manual entries by ISBN/DOI/URL. A bulk import path lives on the v2 backlog.",
      },
      {
        q: "Do excerpts share their source's metadata?",
        a: "Yes — that's the point. Author and publication on an excerpt are derived from the linked library item, so there's one source of truth, not two.",
      },
    ],
    crossLinks: ["my-excerpts", "readwise-sync", "composer"],
  },

  // --- 8 ----------------------------------------------------------------
  {
    slug: "my-excerpts",
    group: "organize",
    navLabel: "My Excerpts",
    navDescription: "Canonical excerpts you can trust forever.",
    title: "My Excerpts — Excerptus",
    description:
      "Save the exact passage with the exact citation. Every excerpt links to a library source, so author, page, and publication are always correct.",
    eyebrow: "Organize",
    h1: "Canonical Excerpts You Can Trust Forever",
    sub: "Excerpts are the heart of Excerptus. Every saved passage carries its author, page, and source — not in your head, not in a doc, but as structured data the rest of the app can act on.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: BookMarked,
        title: "Linked to a library source",
        body: "Every excerpt points to a library item. Change the source's author? Every excerpt updates. One source of truth, no drift.",
      },
      {
        icon: Tags,
        title: "Codex-driven topics and tags",
        body: "Topics come from your Knowledge Worker Codex; tags are free-form. Both are searchable, both filter the list.",
      },
      {
        icon: PenLine,
        title: "Flow into your Drafts",
        body: "Drop excerpts straight into a Composer Draft. Citation and author come along — no manual footnote work.",
      },
      {
        icon: ImageIcon,
        title: "Render as share images",
        body: "Any excerpt can become a branded image in Image Studio, with the attribution already correct.",
      },
    ],
    faq: [
      {
        q: "Why not just use Notion or Obsidian for this?",
        a: "You can — and we recommend keeping them. But excerpts in Excerptus carry real citation metadata the moment they're saved, and they flow into the Composer and Image Studio without conversion.",
      },
      {
        q: "Can I edit an excerpt's author independently of its source?",
        a: "No — and that's deliberate. The library item is the single source of truth. Edit the source; the excerpt follows. This is what 'canonical' means.",
      },
      {
        q: "Where do Readwise highlights fit?",
        a: "They live in a separate Readwise Highlights page. Promote the ones worth canonizing into proper Excerpts; leave the rest in the highlights inbox.",
      },
    ],
    crossLinks: ["my-library", "composer", "image-studio"],
  },

  // --- 9 ----------------------------------------------------------------
  {
    slug: "notes",
    group: "capture",
    navLabel: "Notes",
    navDescription: "Plain-text notes with folders. Just capture ideas quickly.",
    title: "Notes — Excerptus",
    description:
      "Plain-text notes with a folder tree. For the half-thoughts that aren't excerpts and aren't drafts — kept out of the way until you need them.",
    eyebrow: "Capture",
    h1: "Plain-text Notes with Folders to Capture Ideas Quickly",

    sub: "Notes is the scratch surface every writer needs but is often unconnected. Plain text, real folders, instant capture from the mobile '+' button. Keep them, file them, ignore them.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: StickyNote,
        title: "Plain text on purpose",
        body: "No bold, no headings, no rich-text wars. Notes is where ideas get caught before they earn a Draft.",
      },
      {
        icon: Workflow,
        title: "Real folders",
        body: "Build your own folder tree. The 'Notes' root is permanent; everything inside is yours to organize and reorganize.",
      },
      {
        icon: PenLine,
        title: "Quick-capture from mobile",
        body: "The mobile '+' button opens the Notes quick-add sheet — capture in three seconds, file later.",
      },
      {
        icon: ShieldCheck,
        title: "Notes belong to Notes",
        body: "Notes are their own table. They're not pretending to be excerpts or Pieces — and they never will.",
      },
    ],
    faq: [
      {
        q: "Why not just use Apple Notes or Obsidian?",
        a: "Keep using them. Notes in Excerptus is for the captures that belong next to your library and excerpts — not your whole personal knowledge base.",
      },
      {
        q: "Can a note become an excerpt or a Piece?",
        a: "Today, copy/paste. A 'promote to excerpt' action sits on the backlog for v2.",
      },
      {
        q: "Where do Notes show up?",
        a: "On their own page and in the right rail of the Composer, between My Library and Workspace Media. They never clutter the main reading or writing surfaces.",
      },
    ],
    crossLinks: ["my-excerpts", "composer", "focused-writer"],
  },

  // --- 10 (retired) ------------------------------------------------------
  // The MicroPosts feature page was removed 2026-07-03 when short-form
  // was cut from Excerptus. The `comingSoon` group is intentionally left
  // in place with no entries so upcoming items can be slotted here.

  // --- 11 ----------------------------------------------------------------
  {
    slug: "focused-writer",
    group: "publish",
    navLabel: "For Focused Writers",
    navDescription: "For writers who just want to write — not run their whole life in an app.",
    title: "For Focused Writers — Excerptus",
    description:
      "Excerptus is the heart of a second brain, not a replacement for one. If you write for a living and don't want to manage your whole life in Notion or Obsidian — this page is for you.",
    eyebrow: "Positioning",
    h1: "For Writers Who Just Want to Write",
    sub: "Excerptus is the heart of a Knowledge Worker's second brain — not the whole organism. Keep Obsidian, Notion, Claude, or whatever you already use. Bring the part that needs to be canonical, citable, and publish-ready here.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: PenLine,
        title: "The writing engine, not the whole stack",
        body: "Excerptus does library, excerpts, Composer, and publishing exceptionally. It doesn't try to be your task manager, your daily planner, or your calendar.",
      },
      {
        icon: Brain,
        title: "Bring your own second brain",
        body: "Already in Obsidian? Notion? Roam? Keep them. Excerptus complements them — the citable research and writing layer slots in next to whatever you already love.",
      },
      {
        icon: Sparkles,
        title: "AI on your terms",
        body: "Use ChatGPT, Claude, or anything else for the rest of your life. In Excerptus, AI is scoped to your library and your Codex — and you can turn it off.",
      },
      {
        icon: ShieldCheck,
        title: "Honest positioning",
        body: "We're not going to tell you to delete the apps you already pay for. We're going to tell you what Excerptus does better — and what it doesn't try to do at all.",
      },
    ],
    callout: {
      title: "What Excerptus is not",
      body: "Not a task manager. Not a calendar. Not a personal knowledge base for the rest of your life. Not a Notion / Obsidian / Roam replacement. Not a newsletter platform. It's the heart of a Knowledge Worker's second brain — the writing-and-research core. That's the whole job.",
    },
    faq: [
      {
        q: "Should I leave Obsidian / Notion / Roam?",
        a: "Probably not. Excerptus is the citable, publish-ready research and writing layer. The rest of your second brain — daily notes, personal knowledge, life planning — stays where it works.",
      },
      {
        q: "What about ChatGPT or Claude?",
        a: "Keep using them. Excerptus has its own AI for writing and planning, scoped to your library and Codex — but it's not trying to be your everyday chat assistant.",
      },
      {
        q: "So what's Excerptus actually for?",
        a: "Building a citable library, capturing canonical excerpts, drafting long-form with your own corpus, and publishing where your readers are. The heart of the second brain — not the whole brain.",
      },
    ],
    crossLinks: ["composer", "my-library", "notes"],
  },

  // --- 11b ---------------------------------------------------------------
  {
    slug: "publisher-panel",
    group: "publish",
    navLabel: "Publisher Panel",
    navDescription: "One inbox for every prepared piece — ship to connectors or paste-and-tick manual destinations.",
    title: "Publisher Panel — Excerptus",
    description:
      "The Publisher Panel is Excerptus's single inbox for prepared pieces. Push to live connectors, fan out to multiple destinations, or copy-and-tick the places that don't have an API.",
    eyebrow: "Write & Publish",
    h1: "One Publisher Panel.\nEvery Destination.",
    sub: "When a Piece is ready, it lands in the Publisher Panel. Send it to live connectors with one click, fan out to multiple destinations at once, or copy-and-tick the places that don't have an API. Nothing about the workflow forces you to leave Excerptus.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: Send,
        title: "Single inbox, per-destination chips",
        body: "Every prepared publication shows up once, with a chip for each destination. You can see at a glance what's shipped, what's pending, and where each Piece went — without leaving the page.",
      },
      {
        icon: Workflow,
        title: "Live connectors, no copy-paste",
        body: "WordPress.com, WordPress self-hosted, Ghost, Kit, Bento, Beehiiv, Buttondown, Substack (unofficial), generic webhook, and email all push directly — one composer, many long-form destinations.",
      },
      {
        icon: Layers,
        title: "One-click multi-destination fan-out",
        body: "Pick two or more destinations from the chip popover and one Send dispatches sequentially. Per-destination errors surface as toasts so a single failure never blocks the rest of the send.",
      },
      {
        icon: ShieldCheck,
        title: "Manual destinations, your way",
        body: "Curate your own copy-and-paste chips per workspace — LinkedIn, Medium, X, anywhere. The body copies to clipboard, you paste, you tick the chip. Same audit trail as live connectors.",
      },
    ],
    callout: {
      title: "Many Publish Types, One Panel",
      body: "Article, Newsletter, Long social, Book chapter (coming soon), Content segment. Each destination chip is filtered by publish type so you only see the destinations that actually fit what you're shipping.",
    },
    faq: [
      {
        q: "What's the difference between a live connector and a manual destination?",
        a: "A live connector is a real API push from Excerptus to the destination. A manual destination is a curated copy-paste chip — Excerptus copies the body to your clipboard, you paste it where it needs to go, and you tick the chip. Either way you get the same record of 'shipped'.",
      },
      {
        q: "What happens to a Piece after I publish it?",
        a: "It stays in Composer › Vault › Archive, organized by folder. Publishing is additive — nothing is destroyed or moved out of reach. You can promote a published Piece to your Corpus later if it represents your canon.",
      },
      {
        q: "Can I publish to several places at once?",
        a: "Yes. The chip popover supports multi-select with sequential send and per-destination error reporting, so a Newsletter can fan out to Kit and a webhook in one click while a separate manual chip copies the body for LinkedIn.",
      },
    ],
    crossLinks: ["composer", "focused-writer", "monetized-citations"],
  },

  // --- 12 ----------------------------------------------------------------

  {
    slug: "my-corpus",
    group: "organize",
    navLabel: "My Corpus",
    navDescription: "Your canon — published work in a primary, secondary, and supplemental hierarchy.",
    title: "My Corpus — Excerptus",
    description:
      "The Corpus is your canon: the published Pieces and source material you want the Composer's agents to ground future Drafts in. Curated, not catch-all.",
    eyebrow: "Organize",
    h1: "Your Corpus — the Canonical Source of Truth for Your Writing",
    sub: "Excerptus separates ancillary publishing (like newsletters, etc.) from your primary work. My Library holds every source you cite; My Corpus holds the work you designate as primary and secondary sources of truth. If you elect to use AI for writing or drafting, this is where those agents look first to keep your writing style and content true to your voice and vision.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: Layers,
        title: "A curated canon you control",
        body: "Promote only the Pieces and references that represent your strongest, most evergreen work. Newsletters and other formats stay in the Archive.",
      },
      {
        icon: Brain,
        title: "Grounds the Composer agents",
        body: "When you draft with AI on, the Planning and Writing agents prefer your Corpus over generic web text. Your prose, your sources, your voice — first.",
      },
      {
        icon: Workflow,
        title: "Promote and demote, any time",
        body: "Every published Piece offers a one-click 'Promote to Corpus'. Change your mind later and demote it back to the Archive. No data loss either way.",
      },
      {
        icon: ShieldCheck,
        title: "Workspace-scoped",
        body: "Each workspace has its own Corpus, so client work, personal work, and side projects never bleed into each other's voice or sources.",
      },
    ],
    callout: {
      title: "Pair with the Knowledge Worker Codex",
      body: "The Codex defines how you write. The Corpus defines what you've already written. Together they make AI-assisted Drafts sound like you, not like a generic model.",
      href: "/features/knowledge-worker-codex/",
      linkLabel: "See the Codex →",
    },
    faq: [
      {
        q: "What's the difference between My Library and My Corpus?",
        a: "My Library is the universe of sources you can cite. My Corpus is the curated subset of your own published work (and chosen reference material) the agents should learn from. Different jobs, both essential.",
      },
      {
        q: "Do I have to use the Corpus?",
        a: "No. You can write in the Composer without ever promoting anything. The Corpus is what makes AI-assisted Drafts feel like an extension of your voice rather than a stranger's.",
      },
      {
        q: "Does promoting a Piece make it public?",
        a: "No. Corpus membership is private to your workspace. It only changes how the Composer's agents ground your future Drafts.",
      },
    ],
    crossLinks: ["composer", "knowledge-worker-codex", "my-excerpts"],
  },

  // --- 13 ----------------------------------------------------------------
  {
    slug: "monetized-citations",
    group: "earn",
    navLabel: "Monetized Citations",
    navDescription: "The books you already cite become Amazon Associate links. (Optional)",
    title: "Monetized Citations — Excerptus",
    description:
      "Helping writers and researchers earn from their published work is part of the Excerptus ethos. Turn the books you already cite into Amazon Associate links — opt in per citation, full control.",
    eyebrow: "Earn",
    h1: "Earn from the Books You Already Cite",
    sub: "The books in your bibliography are working for your readers. They can work for you, too. Monetized Citations turns book references into Amazon Associate links — opt in per citation, with granular controls over when and how.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: DollarSign,
        title: "Your books, your affiliate revenue",
        body: "Bring your own Amazon\u00A0Associates tag (and optional\u00A0PA-API credentials). Excerptus enriches book citations with subtle, theme-aware 'opens on Amazon' links — you keep 100% of the commission.",
      },
      {
        icon: Quote,
        title: "Granular per-citation control",
        body: "Monetization is opt-in per citation, not all-or-nothing. Turn it on for the books you genuinely recommend; leave it off everywhere else.",
      },
      {
        icon: ShieldCheck,
        title: "Honest by design",
        body: "Locked tooltip wording: 'Opens on Amazon — purchases support the author.' Readers always know what they're clicking. No dark patterns, ever.",
      },
      {
        icon: Sparkles,
        title: "Part of the Excerptus ethos",
        body: "Knowledge workers deserve to earn from the work they publish. Monetized Citations is one of the optional revenue rails Excerptus builds in — not bolt-on growth hacks.",
      },
    ],
    callout: {
      title: "Also Earn by Referring Other Writers",
      body: "Excerptus Affiliates pays a lifetime recurring commission on every paying user you refer. Two earn rails, one workspace.",
      href: "/features/excerptus-affiliates/",
      linkLabel: "See Excerptus Affiliates →",
    },
    faq: [
      {
        q: "Do I need an Amazon Associates account?",
        a: "Yes — you bring your own Associates tag and your own Amazon PA-API credentials. Excerptus never uses a platform-wide tag; the commission is yours, not ours.",
      },
      {
        q: "Is monetization on by default?",
        a: "No. It's opt-in per citation. Nothing on your site changes until you flip it on for a specific book reference.",
      },
      {
        q: "What does the reader see?",
        a: "The book title becomes a subtle link with a dotted underline inheriting your theme's link color. Hovering reveals: 'Opens on Amazon — purchases support the author.' No banners, no popups, no chrome.",
      },
    ],
    crossLinks: ["my-library", "excerptus-affiliates", "composer"],
  },

  // --- 14 ----------------------------------------------------------------
  {
    slug: "excerptus-affiliates",
    group: "earn",
    navLabel: "Excerptus Affiliates",
    navDescription: "Excerptus users only. Lifetime recurring commissions. (Optional)",
    title: "Excerptus Affiliates — Excerptus",
    description:
      "Refer other writers and researchers to Excerptus and earn a lifetime recurring commission on every paying customer. Open to every paid plan, Starter through Agency.",
    eyebrow: "Earn",
    h1: "Lifetime Recurring Commissions for the Writers You Refer",
    sub: "If Excerptus is making your work better, tell another writer. Every paying customer you refer pays you a recurring commission — for as long as they stay subscribed.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: DollarSign,
        title: "15% lifetime recurring",
        body: "Every paying user you refer pays you 15% of their subscription, every month, for the life of that subscription, for as long as you remain an Excerptus account holder. No limited or first-month-only nonsense.",
      },
      {
        icon: Users,
        title: "Open to every paid plan",
        body: "Starter, Plus, Pro, Elite, and Agency users can all be affiliates. If you're paying for Excerptus, you can refer other writers and earn.",
      },
      {
        icon: ShieldCheck,
        title: "Stripe Connect payouts",
        body: "Payouts run through Stripe Connect — clean, traceable, and on a predictable cadence. Your dashboard shows clicks, conversions, and pending payouts in real time.",
      },
      {
        icon: Sparkles,
        title: "Affiliate-only — never spammy",
        body: "Only current Excerptus customers can become affiliates. We're not building a generic referral program; we're rewarding the writers who already trust the product.",
      },
    ],
    callout: {
      title: "Pair with Monetized Citations",
      body: "Earn from the books you cite and from the writers you refer. Two clean, optional rails — both honest, both yours to control.",
      href: "/features/monetized-citations/",
      linkLabel: "See Monetized Citations →",
    },
    faq: [
      {
        q: "Who can join?",
        a: "Any active paid Excerptus customer — Starter through Agency. The free trial doesn't qualify; once you're on a paid plan, the affiliate dashboard appears in your account.",
      },
      {
        q: "What's the commission rate?",
        a: "15% of each referred customer's subscription, recurring for the life of that subscription. Locked at 15% — we don't change it.",
      },
      {
        q: "How are payouts handled?",
        a: "Through Stripe Connect. You'll connect a Stripe payout account once and payouts run on a regular cadence after that.",
      },
      {
        q: "Can I refer myself or use my own referral link?",
        a: "No. Self-referrals are filtered out automatically. The program is for net-new customers you've actually introduced to Excerptus.",
      },
    ],
    crossLinks: ["monetized-citations", "agencies", "composer"],
  },

  // --- 15 ----------------------------------------------------------------
  {
    slug: "agencies",
    group: "earn",
    navLabel: "For Agencies",
    navDescription: "Per-workspace seats. Invite VAs to the workspaces they actually need.",
    title: "For Agencies — Excerptus",
    description:
      "The Agency plan gives you unlimited team seats and per-workspace access control — invite Virtual Assistants and contractors only to the workspaces they're actually working in.",
    eyebrow: "Earn",
    h1: "Excerptus for Agencies and Writing Teams",
    sub: "If you run a content studio, a research practice, or a writing agency, Excerptus is built for the way you actually work — one workspace per client, scoped access for each contractor, and the same citable library and Composer underneath.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: Building2,
        title: "One workspace per client",
        body: "Every Agency account gets multiple workspaces, each fully isolated — separate library, separate Corpus, separate voice profile, separate publishing destinations.",
      },
      {
        icon: Users,
        title: "Invite VAs to specific workspaces",
        body: "Add a Virtual Assistant to the client workspace they're working in — not your whole account. Two seats per workspace, and team members across the account.",
      },
      {
        icon: Layers,
        title: "Shared engine, scoped data",
        body: "Every workspace runs the full Excerptus stack: My Library, My Excerpts, My Corpus, Composer, Knowledge Worker Codex. The plumbing is shared; the data never bleeds across clients.",
      },
      {
        icon: Workflow,
        title: "Per-workspace cost breakdown",
        body: "AI usage, addons, and credit consumption are tracked per workspace so you can bill client work accurately and spot the workspaces that warrant a tier change.",
      },
    ],
    callout: {
      title: "Refer Other Agencies, Earn Recurring",
      body: "Agency accounts can also be affiliates. Refer a peer studio to Excerptus and earn 15% lifetime recurring on their subscription.",
      href: "/features/excerptus-affiliates/",
      linkLabel: "See Excerptus Affiliates →",
    },
    faq: [
      {
        q: "How many workspaces and seats does Agency include?",
        a: "Unlimited team members across the account, with two seats per workspace. Workspace count scales with the Agency tier — see the pricing page for the current allocation.",
      },
      {
        q: "Can a VA see all of my client workspaces?",
        a: "No. A team member only sees and acts in the specific workspaces you invite them to. Per-workspace access is the whole point of the model.",
      },
      {
        q: "Do clients get their own logins?",
        a: "Today, the model is contractor-and-team-first — you invite VAs and collaborators into the workspaces you run. A client-review surface sits on the v2 backlog.",
      },
    ],
    crossLinks: ["excerptus-affiliates", "composer", "my-library"],
  },

  // --- Meta / stance page (hidden from mega-menu; linked from homepage) -----
  {
    slug: "desktop-and-mobile",
    group: "organize",
    omitFromNav: true,
    navLabel: "Desktop & Mobile",
    navDescription: "Desktop for Deep Work. Mobile for Capture.",
    title: "Desktop & Mobile — Excerptus",
    description:
      "Excerptus is desktop-first for deep research and writing, mobile-first for capture and review — one workspace, two postures, no compromises.",
    eyebrow: "Built for knowledge work",
    h1: "Desktop for Deep Work. Mobile for Capture.",
    sub: "Excerptus is different than other web apps. Research, long-form writing, and publishing want a real screen — so the desktop experience is uncompromising. Capture, review, and reading often need your phone — so the mobile experience is fast, focused, and first-class for the things you actually do on the go.",
    ...TRIAL_CTA,
    blocks: [
      {
        icon: PenLine,
        title: "Desktop is where you do the work",
        body: "Composer's 3-pane Draft Canvas, Image Studio, Library curation, Admin, and dense settings are designed for a real workstation. No compromise to fit a 390px viewport.",
      },
      {
        icon: StickyNote,
        title: "Mobile is where you capture",
        body: "A bottom tab bar with Excerpts, Library, Notes, Readwise, and More. The \u201C+\u201D button opens a Notes quick-add sheet so a thought becomes a saved note in two taps.",
      },
      {
        icon: Layers,
        title: "Consume and review anywhere",
        body: "Read Readwise highlights, triage your Feed Reader inbox, browse Podcasts and your Library on the train. Everything stays in sync because there's one workspace, not two apps.",
      },
      {
        icon: ShieldCheck,
        title: "A focused phone, on purpose",
        body: "Mobile isn't a shrunk-down desktop. It surfaces the things that belong on a phone — Notes, Excerpts, Library, Readwise, Feed Reader, Podcasts — and leaves the heavy creation, curation, and admin surfaces where they belong: on a real screen. Less to wade through on the go, more room to think when you sit down to work.",
        wide: true,
      },
    ],
    callout: {
      title: "Same Workspace, Two Approaches.",
      body: "Excerptus is one product with one database — your phone and your desktop are different doors into the same second brain.",
    },
    faq: [
      {
        q: "Can I write a long-form Piece on my phone?",
        a: "You can, but the Composer is built for a real screen. We recommend capturing the idea as a Note on mobile and opening the Composer on desktop when it's time to write.",
      },
      {
        q: "Is there a native iOS or Android app?",
        a: "Not today. Excerptus is a web app — installable as a PWA on iOS and Android — and the mobile web experience is shaped specifically for capture and review.",
      },
      {
        q: "Why no service worker or offline mode?",
        a: "Excerptus is online-first because your library, excerpts, and Codex live in the cloud and stay synced across devices. Offline drafting is on the post-launch consideration list.",
      },
      {
        q: "I'm a mobile-first user — is Excerptus for me?",
        a: "Yes, for capture and review. The honest answer: the deep research and publishing workflows that make Excerptus different are designed for a larger screen. Most users do both — capture on phone, finish on desktop.",
      },
    ],
    crossLinks: ["notes", "my-excerpts", "composer"],
  },
];


export const FEATURE_PAGES_BY_SLUG: Record<string, FeaturePage> = Object.fromEntries(
  FEATURE_PAGES.map((p) => [p.slug, p]),
);

export const FEATURE_GROUPS: Array<{ key: FeatureGroup; label: string }> = [
  { key: "organize", label: "Organize" },
  { key: "capture", label: "Capture" },
  { key: "publish", label: "Write & Publish" },
  { key: "earn", label: "Earn" },
  { key: "comingSoon", label: "Coming Soon" },
];

/**
 * Explicit per-group display order. Slugs not listed here fall to the
 * bottom in registry order. Keep this list in sync with the mega menu
 * IA — it's what powers the visible ordering in MarketingNav.
 */
const NAV_ORDER: Record<FeatureGroup, string[]> = {
  organize: ["my-library", "my-excerpts", "my-corpus", "knowledge-worker-codex"],
  capture: ["readwise-sync", "feed-reader", "podcasts-feed-reader", "notes"],
  publish: ["composer", "focused-writer", "publisher-panel", "image-studio"],
  earn: ["monetized-citations", "excerptus-affiliates", "agencies"],
  comingSoon: [],
};

export const groupedFeaturePages = (): Record<FeatureGroup, FeaturePage[]> => {
  const out: Record<FeatureGroup, FeaturePage[]> = {
    capture: [],
    organize: [],
    publish: [],
    earn: [],
    comingSoon: [],
  };

  for (const p of FEATURE_PAGES) {
    if (p.omitFromNav) continue;
    out[p.group].push(p);
  }
  for (const key of Object.keys(out) as FeatureGroup[]) {
    const order = NAV_ORDER[key];
    out[key].sort((a, b) => {
      const ai = order.indexOf(a.slug);
      const bi = order.indexOf(b.slug);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }
  return out;
};

export const featurePagePath = (slug: string) => `/features/${slug}/`;

